// Match the compact peak file already produced by the media worker.
export const WAVEFORM_PEAKS = 80;
const localPeaks = new WeakMap();
let decodeQueue = Promise.resolve();

export function validateWaveform(value) {
  return Array.isArray(value) && value.length > 0 && value.length <= 1024
    && value.every(peak => typeof peak === 'number' && Number.isFinite(peak) && peak >= 0 && peak <= 1)
    ? value : null;
}

export function audioBufferPeaks(buffer) {
  const peaks = Array(WAVEFORM_PEAKS).fill(0);
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const samples = buffer.getChannelData(channel);
    for (let bucket = 0; bucket < peaks.length; bucket += 1) {
      const start = Math.floor(bucket * samples.length / peaks.length);
      const end = Math.floor((bucket + 1) * samples.length / peaks.length);
      for (let index = start; index < end; index += 1) {
        peaks[bucket] = Math.max(peaks[bucket], Math.abs(samples[index]));
      }
    }
  }
  return peaks.map(peak => Math.min(1, peak));
}

export function recordingWaveform(blob) {
  if (!blob) return Promise.resolve(null);
  if (localPeaks.has(blob)) return localPeaks.get(blob);
  // Decode one local take at a time, resampled to 8 kHz. Retain only 80 peaks,
  // never a full PCM buffer or live audio context for every draft recording.
  const request = decodeQueue.then(async () => {
    const Context = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (!Context) return null;
    const context = new Context(1, 1, 8000);
    return audioBufferPeaks(await context.decodeAudioData(await blob.arrayBuffer()));
  }).catch(() => null);
  localPeaks.set(blob, request);
  decodeQueue = request.then(() => undefined);
  return request;
}

export async function fetchWaveform(url, signal) {
  // The URL already includes a short-lived playback ticket. Do not attach
  // account headers or download/decode the full remote audio for this graphic.
  const response = await fetch(url, { signal, credentials: 'omit', cache: 'no-store' });
  if (!response.ok) return null;
  return validateWaveform(await response.json());
}

export function waveformPath(peaks, count) {
  // Aggregate for narrow screens; keep silence flat instead of inventing waves.
  const bars = Array.from({ length: count }, (_, index) => {
    let peak = 0;
    if (peaks?.length) {
      const start = Math.floor(index * peaks.length / count);
      const end = Math.max(start + 1, Math.floor((index + 1) * peaks.length / count));
      for (let j = start; j < end; j += 1) peak = Math.max(peak, peaks[j] || 0);
    }
    return peak;
  });
  const maximum = Math.max(.01, ...bars);
  return bars.map((peak, index) => {
    const height = Math.max(1, peak / maximum * 20);
    return `M${index * 5 + 2.5},${12 - height / 2}v${height}`;
  }).join('');
}
